/*
  Colega Link · Blog editorial propio
  Ruta pública: /blog
  Sin dependencias de Trysoro. Conserva el artículo local de delegaciones y
  muestra únicamente publicaciones propias provenientes del backend.
*/
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  Cpu,
  HeartHandshake,
  Scale,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const CATEGORIES = [
  {
    id: "delegaciones",
    label: "Delegaciones jurídicas",
    description: "Audiencias, diligencias, colaboración territorial y buenas prácticas entre colegas.",
    icon: Scale,
    accent: "border-amber-300/25 bg-amber-300/10 text-amber-200",
  },
  {
    id: "mediacion",
    label: "Mediación familiar",
    description: "Orientación clara para mediadores y personas que buscan una solución colaborativa.",
    icon: HeartHandshake,
    accent: "border-teal-300/25 bg-teal-300/10 text-teal-200",
  },
  {
    id: "tecnologia",
    label: "Tecnología jurídica",
    description: "LegalTech, inteligencia artificial y herramientas para una práctica profesional más eficiente.",
    icon: Cpu,
    accent: "border-cyan-300/25 bg-cyan-300/10 text-cyan-200",
  },
];

const FEATURED_ARTICLE = {
  slug: "delegar-gestion-juridica-whatsapp-riesgos",
  title: "Delegar una gestión jurídica por WhatsApp: riesgos y una alternativa más ordenada",
  summary: "Identidad profesional, alcance, honorarios, pago y evidencia: lo que debería quedar claro antes de confiar una audiencia o diligencia.",
  category: "delegaciones",
  categoryLabel: "Delegaciones jurídicas",
  author: "Equipo ColegaLink",
  publishedAt: "2026-08-31T12:00:00-04:00",
  path: "/blog/delegar-gestion-juridica-whatsapp-riesgos",
  featured: true,
};


const STATIC_ARTICLES = [
  {
    slug: "primera-sesion-mediacion-familiar-como-prepararse",
    title: "Primera sesión de mediación familiar: cómo prepararse y qué esperar",
    summary: "Qué antecedentes reunir, cuál es el rol del mediador y cómo participar en una conversación orientada a acuerdos claros y cumplibles.",
    category: "mediacion",
    categoryLabel: "Mediación familiar",
    author: "Equipo ColegaLink",
    publishedAt: "2026-09-01T09:00:00-04:00",
    path: "/blog/primera-sesion-mediacion-familiar-como-prepararse",
  },
  {
    slug: "inteligencia-artificial-practica-juridica-uso-responsable",
    title: "Inteligencia artificial en la práctica jurídica: 7 controles antes de usarla",
    summary: "Un método práctico para trabajar con IA manteniendo revisión humana, trazabilidad, fuentes verificables y resguardo de datos.",
    category: "tecnologia",
    categoryLabel: "Tecnología jurídica",
    author: "Equipo ColegaLink",
    publishedAt: "2026-09-01T09:30:00-04:00",
    path: "/blog/inteligencia-artificial-practica-juridica-uso-responsable",
  },
];

const BLOCKED_EXTERNAL_SLUGS = new Set([
  "como-la-ley-21-226-cambio-la-mediacion-familiar-en-chile",
]);

function setSEO() {
  const title = "Blog ColegaLink · Delegaciones, mediación y tecnología jurídica";
  const description = "Guías y análisis para abogados, mediadores familiares y personas que buscan servicios jurídicos en Chile.";
  const url = window.location.origin + "/blog";
  document.title = title;

  const setMeta = (attribute, key, content) => {
    let element = document.querySelector("meta[" + attribute + '=\"' + key + '\"]');
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attribute, key);
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };

  setMeta("name", "description", description);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:type", "website");
  setMeta("property", "og:url", url);
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString("es-CL", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function inferCategory(post) {
  const tags = (post.tags || []).map((tag) => String(tag).toLowerCase());
  const text = [post.title, post.summary, ...tags].filter(Boolean).join(" ").toLowerCase();

  if (text.includes("mediaci")) return "mediacion";
  if (
    text.includes("legaltech") ||
    text.includes("tecnolog") ||
    text.includes("inteligencia artificial") ||
    tags.includes("ia")
  ) {
    return "tecnologia";
  }
  return "delegaciones";
}

function normalizePost(post) {
  const category = inferCategory(post);
  const categoryInfo = CATEGORIES.find((item) => item.id === category);
  return {
    ...post,
    category,
    categoryLabel: categoryInfo?.label || "Actualidad jurídica",
    publishedAt: post.published_at || post.created_at,
    path: "/blog/" + post.slug,
  };
}

function ArticleCard({ article }) {
  return (
    <Link
      to={article.path}
      className="group flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.04] p-6 text-white transition duration-300 hover:-translate-y-1 hover:border-teal-300/40 hover:bg-white/[0.07]"
    >
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="font-bold uppercase tracking-[0.16em] text-teal-300">
          {article.categoryLabel}
        </span>
        {article.featured && (
          <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-amber-200">
            Destacado
          </span>
        )}
      </div>

      <h2 className="mt-5 text-2xl font-black leading-tight transition group-hover:text-teal-200">
        {article.title}
      </h2>
      <p className="mt-4 flex-1 leading-7 text-slate-300">{article.summary}</p>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-5 text-sm text-slate-400">
        <span className="inline-flex items-center gap-2">
          <CalendarDays size={15} />
          {formatDate(article.publishedAt)}
        </span>
        <span className="inline-flex items-center gap-2 font-bold text-white">
          Leer artículo <ArrowRight size={16} className="transition group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [remotePosts, setRemotePosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("todos");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSEO();
  }, []);

  useEffect(() => {
    let cancelled = false;

    api
      .get("/blog/posts")
      .then((response) => {
        if (cancelled) return;
        const posts = Array.isArray(response.data) ? response.data : response.data?.posts || [];
        const ownedPosts = posts
          .filter((post) => post?.published !== false)
          .filter((post) => post?.created_by !== "soro")
          .filter((post) => !BLOCKED_EXTERNAL_SLUGS.has(post?.slug))
          .map(normalizePost);
        setRemotePosts(ownedPosts);
      })
      .catch(() => {
        if (!cancelled) setRemotePosts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const articles = useMemo(() => [FEATURED_ARTICLE, ...STATIC_ARTICLES, ...remotePosts], [remotePosts]);
  const visibleArticles = useMemo(() => {
    if (activeCategory === "todos") return articles;
    return articles.filter((article) => article.category === activeCategory);
  }, [activeCategory, articles]);

  return (
    <main data-testid="blog-page" className="min-h-screen bg-slate-950 text-white">
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(45,212,191,0.16),transparent_38%),radial-gradient(circle_at_left,rgba(251,191,36,0.10),transparent_34%)]" />
        <div className="container-app relative py-16 md:py-24">
          <div className="inline-flex items-center gap-2 rounded-full border border-teal-300/25 bg-teal-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-200">
            <BookOpen size={14} /> ColegaLink · Blog
          </div>

          <div className="mt-7 grid gap-10 lg:grid-cols-[1.35fr_0.65fr] lg:items-end">
            <div>
              <h1 className="max-w-4xl text-4xl font-black leading-[1.05] md:text-6xl">
                Conocimiento jurídico para <span className="brand-gradient-text">colaborar mejor.</span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-300">
                Guías y análisis claros para abogados, mediadores familiares y personas que necesitan orientación antes de tomar una decisión.
              </p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.05] p-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-1 text-teal-300" size={24} />
                <div>
                  <p className="font-black">Contenido propio y revisado</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    ColegaLink publica información práctica con fuentes identificables y distingue la orientación general de la asesoría jurídica.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-app py-14">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-300">Líneas editoriales</p>
        <h2 className="mt-3 text-3xl font-black">Tres áreas para resolver dudas reales.</h2>

        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setActiveCategory(category.id)}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 text-left transition hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.06]"
              >
                <span className={"inline-flex rounded-2xl border p-3 " + category.accent}>
                  <Icon size={22} />
                </span>
                <h3 className="mt-5 text-xl font-black">{category.label}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-300">{category.description}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/10 bg-black/25">
        <div className="container-app py-14">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300">Biblioteca editorial</p>
              <h2 className="mt-3 text-3xl font-black">Artículos de ColegaLink</h2>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCategory("todos")}
                className={
                  "rounded-full border px-4 py-2 text-sm font-bold transition " +
                  (activeCategory === "todos"
                    ? "border-teal-300 bg-teal-300 text-slate-950"
                    : "border-white/15 text-slate-300 hover:border-white/30")
                }
              >
                Todos
              </button>
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategory(category.id)}
                  className={
                    "rounded-full border px-4 py-2 text-sm font-bold transition " +
                    (activeCategory === category.id
                      ? "border-teal-300 bg-teal-300 text-slate-950"
                      : "border-white/15 text-slate-300 hover:border-white/30")
                  }
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="mt-9 rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-slate-300">Cargando artículos…</div>
          ) : visibleArticles.length > 0 ? (
            <div className="mt-9 grid gap-6 lg:grid-cols-2">
              {visibleArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-9 rounded-3xl border border-dashed border-white/15 bg-white/[0.03] p-9">
              <Sparkles className="text-amber-300" size={24} />
              <h3 className="mt-4 text-xl font-black">Estamos preparando esta sección.</h3>
              <p className="mt-2 max-w-2xl leading-7 text-slate-300">
                Muy pronto encontrarás nuevas guías elaboradas por ColegaLink para esta línea editorial.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="container-app py-16">
        <div className="overflow-hidden rounded-3xl border border-teal-300/25 bg-gradient-to-br from-teal-950 to-slate-950 p-8 md:p-12">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-300">Conecta · Delega · Resuelve</p>
          <h2 className="mt-4 max-w-3xl text-3xl font-black md:text-4xl">Lleva la colaboración jurídica a un flujo más claro.</h2>
          <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-300">
            Conoce los planes para abogados o encuentra mediadores familiares verificados dentro de ColegaLink.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/planes" className="inline-flex items-center gap-2 rounded-xl bg-teal-300 px-5 py-3 font-black text-slate-950 transition hover:bg-teal-200">
              Ver planes <ArrowRight size={17} />
            </Link>
            <Link to="/mediacion-familiar" className="inline-flex items-center gap-2 rounded-xl border border-white/20 px-5 py-3 font-black text-white transition hover:bg-white/10">
              Conocer mediación familiar
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
